import React, { useState, useEffect } from "react";
import Sidebaradmin from "../Sidebar";
import Navbaradmin from "../navbar";
import DynamicPricingAdminPage from "./AdminDynamicPricing";

const ProductAdmin = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    stock: "",
    images: "",
    isActive: true,
    subscriptionPlan: "", // New field for Subscription Plan
    months: 1, // Default to 1 month
    totalPrice: 0, // Total price based on months and price
    planID: "", // New field
    additionalDetails: "", // New field
    promotion: "", // New field
    cancelledMRP: "", // New field
  });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch all products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/products");
      const data = await response.json();
      if (response.ok) {
        setProducts(data);
      } else {
        setError("Failed to fetch products");
      }
    } catch (err) {
      setError("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updatedForm = { ...prev, [name]: value };
      if (
        name === "subscriptionPlan" ||
        name === "months" ||
        name === "price"
      ) {
        updatedForm.totalPrice = calculateTotalPrice(
          updatedForm.price,
          updatedForm.subscriptionPlan,
          updatedForm.months
        );
      }
      return updatedForm;
    });
  };

  // Calculate total price based on subscription plan and months
  const calculateTotalPrice = (price, plan, months) => {
    const pricePerMonth = parseFloat(price); // Use the product's price
    if (plan === "StarterPlan") {
      return pricePerMonth * months; // Same price for StarterPlan
    } else if (plan === "AdvancedPlan") {
      return pricePerMonth * months; // 1.5x price for AdvancedPlan
    } else if (plan === "ProPlan") {
      return pricePerMonth * months; // 2x price for ProPlan
    }
    return pricePerMonth * months; // Default to the product price if no plan is selected
  };

  // Handle product submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editId ? "PUT" : "POST";
      const url = editId
        ? `/api/admin/products/${editId}`
        : "/api/admin/products";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (response.ok) {
        setForm({
          name: "",
          description: "",
          price: "",
          category: "",
          stock: "",
          images: "",
          isActive: true,
          subscriptionPlan: "",
          months: 1,
          totalPrice: 0,
          planID: "", // Reset the new field
          additionalDetails: "", // Reset the new field
          promotion: "", // Reset the new field
          cancelledMRP: "", // Reset the new field
        });
        setEditId(null);
        fetchProducts();
      } else {
        setError("Failed to save product");
      }
    } catch (err) {
      setError("Failed to save product");
    }
  };

  // Edit a product
  const handleEdit = (product) => {
    setEditId(product._id);
    setForm({
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      stock: product.stock,
      images: product.images.join(","),
      isActive: product.isActive,
      subscriptionPlan: product.subscriptionPlan || "",
      months: product.months || 1,
      totalPrice: product.totalPrice || 0,
      planID: product.planID || "", // New field
      additionalDetails: product.additionalDetails || "", // New field
      promotion: product.promotion || "", // New field
      cancelledMRP: product.cancelledMRP || "", // New field
    });
  };

  // Delete a product
  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/admin/products/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        fetchProducts();
      } else {
        setError("Failed to delete product");
      }
    } catch (err) {
      setError("Failed to delete product");
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      <Sidebaradmin />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbaradmin />

        <div className="p-8 flex-1 space-y-6 bg-white shadow-lg rounded-lg">
          <h1 className="text-3xl font-semibold text-gray-800">
            Product Admin
          </h1>
          {error && <p className="text-red-500">{error}</p>}

          {/* Add/Edit Product Form */}
          <div className="card bg-white p-6 rounded-lg shadow-md mt-8">
            <h2 className="text-2xl font-semibold mb-4">
              {editId ? "Edit Product" : "Add New Product"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Product Name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md"
                  required
                />
                <textarea
                  name="description"
                  placeholder="Product Description"
                  value={form.description}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md"
                  rows="4"
                />
                <input
                  type="number"
                  name="price"
                  placeholder="Price"
                  value={form.price}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md"
                  required
                />
                <input
                  type="text"
                  name="category"
                  placeholder="Category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md"
                />
                <input
                  type="number"
                  name="stock"
                  placeholder="Stock Quantity"
                  value={form.stock}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md"
                />
                <input
                  type="text"
                  name="images"
                  placeholder="Image URLs (comma-separated)"
                  value={form.images}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md"
                />
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                    className="text-blue-600"
                  />
                  <span>Active</span>
                </label>

                {/* Subscription Plan Field */}
                <div className="space-y-2">
                  <label className="font-semibold">Subscription Plan</label>
                  <select
                    name="subscriptionPlan"
                    value={form.subscriptionPlan}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-md"
                  >
                    <option value="">Select Subscription Plan</option>
                    <option value="StarterPlan">Starter Plan</option>
                    <option value="AdvancedPlan">Advanced Plan</option>
                    <option value="ProPlan">Pro Plan</option>
                  </select>
                </div>

                {/* Months Field */}
                <div className="space-y-2">
                  <label className="font-semibold">Number of Months</label>
                  <input
                    type="number"
                    name="months"
                    value={form.months}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-md"
                    min="1"
                  />
                </div>

                {/* Total Price Field */}
                <div className="space-y-2">
                  <label className="font-semibold">Total Price</label>
                  <input
                    type="text"
                    name="totalPrice"
                    value={`${form.totalPrice}`}
                    readOnly
                    className="w-full p-3 border rounded-md bg-gray-100"
                  />
                </div>

                {/* New Fields */}
                <div className="space-y-2">
                  <label className="font-semibold">Plan ID</label>
                  <input
                    type="text"
                    name="planID"
                    value={form.planID}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-md"
                  />
                </div>

                <div className="space-y-2">
                  <label className="font-semibold">Additional Details</label>
                  <textarea
                    name="additionalDetails"
                    value={form.additionalDetails}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-md"
                    rows="4"
                  />
                </div>

                <div className="space-y-2">
                  <label className="font-semibold">Promotion</label>
                  <input
                    type="text"
                    name="promotion"
                    value={form.promotion}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-md"
                  />
                </div>

                <div className="space-y-2">
                  <label className="font-semibold">Cancelled MRP</label>
                  <input
                    type="number"
                    name="cancelledMRP"
                    value={form.cancelledMRP}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-md"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 rounded-md shadow-lg hover:bg-blue-500 transition-colors"
                >
                  {editId ? "Update" : "Create"} Product
                </button>
              </div>
            </form>
          </div>

          {/* Product List Section */}
          {/* Product List Section */}
          <div className="card bg-white p-6 rounded-lg shadow-md mt-8">
            <h2 className="text-2xl font-semibold mb-4">Product List</h2>
            {loading ? (
              <div className="text-center text-gray-500">Loading...</div>
            ) : (
              <table className="min-w-full table-auto border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-4 text-left">Name</th>
                    <th className="p-4 text-left">Price</th>
                    <th className="p-4 text-left">Category</th>
                    <th className="p-4 text-left">Stock</th>
                    <th className="p-4 text-left">Subscription Plan</th>
                    <th className="p-4 text-left">Months</th>
                    <th className="p-4 text-left">Total Price</th>
                    <th className="p-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr
                      key={product._id}
                      className="hover:bg-gray-50 transition-all duration-200"
                    >
                      <td className="p-4 border-b">{product.name}</td>
                      <td className="p-4 border-b">${product.price}</td>
                      <td className="p-4 border-b">{product.category}</td>
                      <td className="p-4 border-b">{product.stock}</td>
                      <td className="p-4 border-b">
                        {product.subscriptionPlan}
                      </td>
                      <td className="p-4 border-b">{product.months}</td>
                      <td className="p-4 border-b">${product.totalPrice}</td>
                      <td className="p-4 border-b">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(product._id)}
                          className="ml-4 text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductAdmin;
